import React, { FunctionComponent, useCallback, useContext } from "react";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import { ScreenSize, UI } from "@wwimmo/ui";
import { RootStoreContext } from "src/stores/RootStore";
import { ISelectableRealestate } from "src/stores/RealestateStore";
import styles from "./NewsRealestatesSection.module.css";

type NewsRealestatesSectionProps = {
    realestates: ISelectableRealestate[];
    onChange: (realestates: ISelectableRealestate[]) => void;
};

const COLUMN_WIDTHS = [1, 1, 0.1];
const REALESTATES_PER_PAGE = 10;

const NewsRealestatesSectionBase: FunctionComponent<NewsRealestatesSectionProps> = ({ realestates, onChange }) => {
    const { t } = useTranslation();
    const { uiStore } = useContext(RootStoreContext);

    // A news always needs at least one realestate.
    const canRemoveRealestate = realestates.length > 1;

    const onRemoveRealestate = useCallback(
        (removedRealestate: ISelectableRealestate) => () => {
            onChange(realestates.filter((realestate) => realestate.id !== removedRealestate.id));
        },
        [onChange, realestates]
    );

    const renderRemoveIcon = useCallback(
        (realestate: ISelectableRealestate) =>
            canRemoveRealestate ? (
                <UI.Icon
                    icon={UI.SVGIcon.Trash}
                    onClick={onRemoveRealestate(realestate)}
                    className={styles.RemoveIcon}
                />
            ) : undefined,
        [canRemoveRealestate, onRemoveRealestate]
    );

    const renderItem = useCallback(
        (realestate: ISelectableRealestate, screenSize: ScreenSize) => {
            const listRows =
                screenSize === ScreenSize.MOBILE
                    ? [
                          <UI.List.Row key={`row-mobile-${realestate.id}`}>
                              <div className={styles.MobileRow}>
                                  <div>
                                      <strong>{realestate.name}</strong>
                                      <div className={styles.MobileNumber}>{realestate.number}</div>
                                  </div>
                                  {renderRemoveIcon(realestate)}
                              </div>
                          </UI.List.Row>
                      ]
                    : [
                          <UI.List.Row key={`row-${realestate.id}`}>
                              <UI.List.Cell key="name" colspan={COLUMN_WIDTHS[0]} value={realestate.name} />
                              <UI.List.Cell key="number" colspan={COLUMN_WIDTHS[1]} value={realestate.number} />
                              <UI.List.Cell
                                  key="remove"
                                  colspan={COLUMN_WIDTHS[2]}
                                  value={renderRemoveIcon(realestate)}
                                  className="d-flex justify-content-center"
                              />
                          </UI.List.Row>
                      ];

            return (
                <UI.List.Item
                    key={`item-${realestate.id}`}
                    screenSize={screenSize}
                    rows={listRows}
                    className={styles.ListItem}
                />
            );
        },
        [renderRemoveIcon]
    );

    const renderHeader = useCallback(
        (screenSize: ScreenSize) => {
            if (screenSize === ScreenSize.MOBILE) {
                return <></>;
            }

            const headerCells = [
                <UI.List.Cell key="name" colspan={COLUMN_WIDTHS[0]} value={t("screens.realestate.title")} truncate />,
                <UI.List.Cell
                    key="number"
                    colspan={COLUMN_WIDTHS[1]}
                    value={t("screens.realestate.dashboard.number")}
                    truncate
                />,
                <UI.List.Cell key="remove" colspan={COLUMN_WIDTHS[2]} value="" />
            ];

            return <UI.List.Header rows={headerCells} className="p-0" />;
        },
        [t]
    );

    return (
        <UI.Row className="mt-4">
            <UI.Col>
                <UI.List.BasicList
                    screenSize={uiStore.isMobile ? ScreenSize.MOBILE : ScreenSize.DESKTOP}
                    items={realestates}
                    renderItem={renderItem}
                    renderHeader={renderHeader}
                    itemsPerPage={REALESTATES_PER_PAGE}
                    emptyListMessage={t("screens.realestate.news_edit.no_realestates_selected").toString()}
                />
            </UI.Col>
        </UI.Row>
    );
};

export const NewsRealestatesSection = observer(NewsRealestatesSectionBase);
